export type ContextMenuInstance = { hide: () => void }

let activeMenu: ContextMenuInstance | null = null
let scrollUnbind: (() => void) | null = null

function isScrollableOverflow(value: string): boolean {
  return value === 'auto' || value === 'scroll' || value === 'overlay'
}

function isScrollableElement(element: HTMLElement): boolean {
  if (typeof getComputedStyle === 'function') {
    const style = getComputedStyle(element)
    if (
      isScrollableOverflow(style.overflow) ||
      isScrollableOverflow(style.overflowX) ||
      isScrollableOverflow(style.overflowY)
    ) {
      return true
    }
  }
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth
}

function asHTMLElement(anchor?: EventTarget | null): HTMLElement | null {
  if (!anchor || typeof anchor !== 'object') {
    return null
  }
  const element = anchor as HTMLElement
  return typeof element.parentElement !== 'undefined' || typeof element.closest === 'function'
    ? element
    : null
}

function collectScrollTargets(anchor?: EventTarget | null): Array<HTMLElement | Window> {
  const targets: Array<HTMLElement | Window> = []
  const seen = new Set<HTMLElement | Window>()

  if (typeof window !== 'undefined') {
    targets.push(window)
    seen.add(window)
  }

  let el = asHTMLElement(anchor)
  while (el) {
    if (isScrollableElement(el) && !seen.has(el)) {
      targets.push(el)
      seen.add(el)
    }
    el = el.parentElement
  }

  return targets
}

function unbindScrollListeners(): void {
  scrollUnbind?.()
  scrollUnbind = null
}

function bindHideOnScroll(anchor: EventTarget | null | undefined, hide: () => void): void {
  unbindScrollListeners()

  const targets = collectScrollTargets(anchor)
  const onScroll = () => hide()

  targets.forEach((target) => {
    target.addEventListener('scroll', onScroll, { capture: true, passive: true })
  })

  scrollUnbind = () => {
    targets.forEach((target) => {
      target.removeEventListener('scroll', onScroll, { capture: true })
    })
  }
}

export function activateContextMenu(menu: ContextMenuInstance, anchor?: EventTarget | null): void {
  if (activeMenu && activeMenu !== menu) {
    activeMenu.hide()
  }

  unbindScrollListeners()
  activeMenu = menu
  bindHideOnScroll(anchor, () => activeMenu?.hide())
}

export function deactivateContextMenu(menu: ContextMenuInstance): void {
  if (activeMenu === menu) {
    activeMenu = null
    unbindScrollListeners()
  }
}

/** Test-only reset of module state. */
export function resetContextMenuCoordinatorForTests(): void {
  activeMenu = null
  unbindScrollListeners()
}
